import React, { useEffect, useState, useRef } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../../components/BottomNav';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, Sparkle, Check, X, ArrowRight, Camera, Target, Lightning, Question } from '@phosphor-icons/react';
import { sparklePoints } from '../../utils/confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Interactive Scanner Tutorial Component
const ScannerTutorial = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0);

  const tutorialSteps = [
    {
      icon: Camera,
      title: "Autorisez la caméra",
      description: "Cliquez sur 'Autoriser' quand votre navigateur demande l'accès à la caméra.",
      tip: "C'est sécurisé et nécessaire pour scanner",
      highlight: "camera"
    },
    {
      icon: Target,
      title: "Centrez le QR code",
      description: "Placez le QR code du marchand bien au centre du cadre doré.",
      tip: "Gardez le téléphone stable",
      highlight: "frame"
    },
    {
      icon: Lightning,
      title: "Scan automatique",
      description: "Le scan est instantané ! Dès que le code est détecté, vous gagnez vos points.",
      tip: "Pas besoin d'appuyer sur un bouton",
      highlight: "auto"
    }
  ];

  const currentStep = tutorialSteps[step];
  const Icon = currentStep.icon;

  const nextStep = () => {
    if (step < tutorialSteps.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10001] bg-[#0F1A14]/95 backdrop-blur-xl"
    >
      {/* Skip button */}
      <button
        onClick={onSkip}
        className="absolute top-6 right-6 text-[#8BA89A] hover:text-white flex items-center gap-2 z-10"
      >
        <span className="text-sm">Passer</span>
        <X size={18} />
      </button>

      {/* Content */}
      <div className="h-full flex flex-col items-center justify-center px-8">
        {/* Visual Demo Area */}
        <motion.div
          key={step}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative mb-8"
        >
          {/* Animated Scanner Preview */}
          <div className="w-64 h-64 relative">
            {/* Outer glow */}
            <div className="absolute -inset-4 bg-[#C3A365]/20 rounded-3xl blur-2xl animate-pulse" />
            
            {/* Frame */}
            <div className="relative w-full h-full bg-[#1C1C1C] rounded-3xl border-2 border-[#C3A365]/50 overflow-hidden">
              {/* Corner brackets */}
              <div className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-[#C3A365]"></div>
              <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-[#C3A365]"></div>
              <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-[#C3A365]"></div>
              <div className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-[#C3A365]"></div>

              {/* Center content based on step */}
              {step === 0 && (
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="text-center">
                    <Camera size={48} className="text-[#C3A365] mx-auto mb-2" weight="duotone" />
                    <p className="text-[#8BA89A] text-xs">Caméra</p>
                  </div>
                </motion.div>
              )}

              {step === 1 && (
                <>
                  {/* Simulated QR code */}
                  <motion.div 
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ scale: 1.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="w-24 h-24 bg-white rounded-lg p-2">
                      <div className="w-full h-full bg-[repeating-linear-gradient(0deg,#000,#000_4px,#fff_4px,#fff_8px),repeating-linear-gradient(90deg,#000,#000_4px,#fff_4px,#fff_8px)]" />
                    </div>
                  </motion.div>
                  
                  {/* Scanning line */}
                  <motion.div
                    className="absolute left-8 right-8 h-0.5 bg-[#C3A365]"
                    initial={{ top: '20%' }}
                    animate={{ top: ['20%', '80%', '20%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  
                  {/* Target arrows pointing inward */}
                  <motion.div
                    className="absolute inset-0"
                    animate={{ scale: [1, 0.95, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="absolute left-2 top-1/2 -translate-y-1/2 text-[#C3A365]" size={24} />
                    <ArrowRight className="absolute right-2 top-1/2 -translate-y-1/2 rotate-180 text-[#C3A365]" size={24} />
                    <ArrowRight className="absolute top-2 left-1/2 -translate-x-1/2 rotate-90 text-[#C3A365]" size={24} />
                    <ArrowRight className="absolute bottom-2 left-1/2 -translate-x-1/2 -rotate-90 text-[#C3A365]" size={24} />
                  </motion.div>
                </>
              )}

              {step === 2 && (
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center"
                  initial={{ scale: 1 }}
                >
                  {/* Success animation */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.5, times: [0, 0.6, 1] }}
                    className="text-center"
                  >
                    <motion.div
                      className="w-20 h-20 mx-auto mb-2 rounded-full bg-[#145A3A] flex items-center justify-center"
                      animate={{ boxShadow: ['0 0 0 0 rgba(195,163,101,0.4)', '0 0 0 20px rgba(195,163,101,0)', '0 0 0 0 rgba(195,163,101,0)'] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <Check size={40} className="text-[#C3A365]" weight="bold" />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="flex items-center gap-1 justify-center"
                    >
                      <Sparkle size={16} className="text-[#C3A365]" weight="fill" />
                      <span className="text-[#C3A365] font-semibold">+10 pts</span>
                    </motion.div>
                  </motion.div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Step indicator */}
        <div className="flex gap-2 mb-6">
          {tutorialSteps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-8 bg-[#C3A365]' : i < step ? 'w-3 bg-[#C3A365]/50' : 'w-3 bg-[#3E5248]'
              }`}
            />
          ))}
        </div>

        {/* Text content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center max-w-sm"
          >
            <div className="inline-flex items-center gap-2 bg-[#283D32] px-3 py-1.5 rounded-full mb-4">
              <Icon size={16} className="text-[#C3A365]" weight="duotone" />
              <span className="text-xs text-[#C3A365] font-medium">Étape {step + 1}/{tutorialSteps.length}</span>
            </div>

            <h2 className="text-2xl text-white font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {currentStep.title}
            </h2>

            <p className="text-[#8BA89A] leading-relaxed mb-4">
              {currentStep.description}
            </p>

            <div className="inline-flex items-center gap-2 bg-[#1E2F26] px-4 py-2 rounded-full">
              <Lightning size={14} className="text-[#C3A365]" weight="fill" />
              <span className="text-[#C3A365] text-sm">{currentStep.tip}</span>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Button */}
        <motion.button
          onClick={nextStep}
          className="mt-8 bg-[#C3A365] text-[#18261E] px-8 py-4 rounded-xl font-semibold flex items-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {step === tutorialSteps.length - 1 ? "C'est parti !" : 'Suivant'}
          <ArrowRight size={18} weight="bold" />
        </motion.button>
      </div>
    </motion.div>
  );
};

// Help Button Component
const HelpButton = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    className="fixed bottom-28 right-5 z-50 w-12 h-12 rounded-full bg-[#283D32] border border-[#3E5248] flex items-center justify-center shadow-lg"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 1 }}
  >
    <Question size={22} className="text-[#C3A365]" weight="duotone" />
  </motion.button>
);

const Scanner = () => {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const scannerInitialized = useRef(false);

  useEffect(() => {
    // Check if user has seen the tutorial
    const tutorialSeen = localStorage.getItem('scanner-tutorial-completed');
    if (!tutorialSeen) {
      setShowTutorial(true);
    }
  }, []);

  useEffect(() => {
    // Only initialize scanner when tutorial is not showing
    if (showTutorial || scannerInitialized.current) return;

    const scanner = new Html5QrcodeScanner('qr-reader', {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1,
      showTorchButtonIfSupported: true,
      showZoomSliderIfSupported: true,
    });

    const onScanSuccess = async (decodedText) => {
      scanner.clear();
      setScanning(true);

      try {
        const { data } = await axios.post(
          `${API_URL}/api/consumer/scan-qr`,
          { qr_code: decodedText },
          { withCredentials: true }
        );
        setScanResult(data);
        toast.success(data.message);
        sparklePoints(); // Golden confetti!
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Scan failed');
      } finally {
        setScanning(false);
      }
    };

    const onScanError = (err) => {
      // Suppress continuous errors
    };

    scanner.render(onScanSuccess, onScanError);
    scannerInitialized.current = true;

    return () => {
      scanner.clear().catch(console.error);
    };
  }, [showTutorial]);

  const handleTutorialComplete = () => {
    localStorage.setItem('scanner-tutorial-completed', 'true');
    setShowTutorial(false);
  };

  const handleTutorialSkip = () => {
    localStorage.setItem('scanner-tutorial-completed', 'true');
    setShowTutorial(false);
  };

  const handleShowHelp = () => {
    setShowTutorial(true);
  };

  return (
    <div className="min-h-screen bg-[#18261E] pb-32">
      {/* Tutorial Overlay */}
      <AnimatePresence>
        {showTutorial && (
          <ScannerTutorial 
            onComplete={handleTutorialComplete} 
            onSkip={handleTutorialSkip}
          />
        )}
      </AnimatePresence>

      {/* Help Button (shows after tutorial) */}
      {!showTutorial && !scanResult && (
        <HelpButton onClick={handleShowHelp} />
      )}

      {/* Header */}
      <div className="px-6 pt-8 pb-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C3A365] font-semibold mb-2">Check In</p>
          <h1 className="text-3xl font-light text-[#E8EBE9] tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="scanner-title">
            Scan QR Code
          </h1>
          <p className="text-[#889990] mt-2">Pointez votre caméra vers le QR code du marchand</p>
        </motion.div>
      </div>

      <div className="px-6">
        {/* QR Scanner with Premium Frame */}
        <motion.div 
          className="relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          data-testid="qr-scanner-container"
        >
          {/* Animated golden border */}
          <div className="absolute -inset-1 rounded-3xl overflow-hidden">
            <motion.div
              className="absolute inset-0"
              style={{
                background: 'conic-gradient(from 0deg, #C3A365, #145A3A, #C3A365, #145A3A, #C3A365)',
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            />
          </div>
          
          {/* Inner container */}
          <div className="relative bg-[#1C1C1C] rounded-3xl p-4 overflow-hidden">
            {/* Corner accents */}
            <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#C3A365]"></div>
            <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-[#C3A365]"></div>
            <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-[#C3A365]"></div>
            <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#C3A365]"></div>
            
            {/* Scanner */}
            <div id="qr-reader" className="rounded-2xl overflow-hidden"></div>
            
            {/* Scanning overlay */}
            {scanning && (
              <div className="absolute inset-4 bg-[#18261E]/80 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Sparkle size={40} className="text-[#C3A365] mx-auto" weight="fill" />
                  </motion.div>
                  <p className="mt-3 text-[#E8EBE9]">Traitement...</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Glow effect */}
          <div className="absolute -inset-8 bg-[#C3A365]/10 blur-3xl rounded-full -z-10 animate-pulse" style={{ animationDuration: '3s' }}></div>
        </motion.div>

        {/* Scan Result */}
        <AnimatePresence>
          {scanResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-6 bg-[#1C1C1C] border border-[#333333] rounded-2xl p-6"
              data-testid="scan-result"
            >
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#145A3A]/30 border border-[#145A3A] flex items-center justify-center"
                >
                  <Check size={32} className="text-[#C3A365]" weight="bold" />
                </motion.div>
                
                <h3 className="text-xl text-[#E8EBE9] font-medium mb-2" data-testid="scan-success-message">
                  {scanResult.message}
                </h3>
                <p className="text-[#C3A365] font-medium" data-testid="merchant-name">
                  {scanResult.merchant_name}
                </p>
                
                {scanResult.points_earned && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-4 inline-flex items-center gap-2 bg-[#C3A365]/20 border border-[#C3A365]/30 px-4 py-2 rounded-full"
                  >
                    <Sparkle size={16} className="text-[#C3A365]" weight="fill" />
                    <span className="text-[#C3A365] font-semibold">+{scanResult.points_earned} points</span>
                  </motion.div>
                )}
                
                {scanResult.visits && (
                  <p className="text-[#889990] mt-4" data-testid="visits-count">
                    Visites totales : <span className="text-[#E8EBE9] font-medium">{scanResult.visits}</span>
                  </p>
                )}
                
                {scanResult.reward_available && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-4 p-4 bg-[#145A3A]/20 border border-[#145A3A]/30 rounded-xl"
                  >
                    <p className="text-[#C3A365] font-semibold">Récompense débloquée !</p>
                    <p className="text-[#E8EBE9] text-sm mt-1">{scanResult.reward_description}</p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Instructions */}
        {!scanResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-6 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-[#283D32] border border-[#3E5248] px-4 py-2 rounded-full">
              <QrCode size={16} className="text-[#C3A365]" />
              <span className="text-xs text-[#889990]">Cherchez le QR code habitué à la caisse</span>
            </div>
          </motion.div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Scanner;
